import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import type { ValidationPhase } from "@/hooks/useRosterValidation";
import { cn } from "@utils/cn.ts";

/**
 * 위반사항 플로팅 버튼 컴포넌트
 * 컴파운드 패턴으로 다양한 상태를 표현
 */

interface ViolationFloatingButtonProps {
  isValidating: boolean;
  violationCount: number;
  phase?: ValidationPhase;
  onClick: () => void;
}

function ViolationFloatingButton({
  isValidating,
  violationCount,
  phase = "idle",
  onClick,
}: ViolationFloatingButtonProps) {
  // 검증 대기 상태: idle, waiting_input이고 검증 중이 아닐 때
  const isWaiting =
    !isValidating && (phase === "idle" || phase === "waiting_input");

  // 상태 결정 (플로팅 메뉴와 동일한 로직)
  const hasResult = phase === "done";
  const isViolation = hasResult && violationCount > 0;
  const isSafe = hasResult && violationCount === 0;

  // 위반사항이 있을 때: 빨간색 테두리, 위반사항이 없을 때: 초록색 테두리
  const borderColor = isViolation
    ? "border-red-500/40"
    : isSafe
      ? "border-green-500/40"
      : "border-gray-200";

  // 배경색: 에러일 때 빨간색, 성공 시 녹색, 검증 대기/중일 때 명확한 회색
  const bgColor =
    isWaiting || isValidating
      ? "bg-gray-200"
      : isViolation
        ? "bg-[#FFEAE9]"
        : isSafe
          ? "bg-[#E2F1E0]"
          : "bg-gray-200";

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "fixed z-20! border-2 shadow-2xl shadow-zinc-900/50 bottom-10 right-10 size-17 rounded-full flex items-center justify-center cursor-pointer transition-colors",
        bgColor,
        borderColor,
        isViolation && "hover:bg-[#FFD5D3]",
        isSafe && "hover:bg-[#D4E9D1]",
        !isViolation && !isSafe && "hover:bg-gray-200"
      )}
    >
      {isWaiting ? (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-medium text-zinc-500">검증 대기</span>
        </div>
      ) : isValidating ? (
        <div className="flex flex-col items-center gap-1">
          <Loader2 className="size-6 animate-spin text-zinc-500" />
          <span className="text-xs font-medium text-zinc-500">검증 중</span>
        </div>
      ) : isSafe ? (
        <>
          <div className="absolute -top-0.5 -right-0.5">
            <div className="text-zinc-50 text-sm relative shadow-2xl z-10! shadow-zinc-900/50 bg-gradient-to-r from-green-400 to-green-600 size-6 flex items-center justify-center rounded-full">
              {violationCount > 99 ? "99+" : violationCount}
            </div>
          </div>

          <div className="flex items-center gap-1 flex-col">
            <ShieldCheck className="size-6 text-[#42AF3A]" />
            <span className="text-xs font-medium text-[#42AF3A]">위반사항</span>
          </div>
        </>
      ) : (
        <>
          <div className="absolute -top-1 -right-2">
            <div className="bg-red-600 z-0! animate-ping px-3 rounded-full size-5 absolute top-[2px] right-1"></div>
            <div className="text-zinc-50 text-xs px-4!  relative shadow-2xl z-10! shadow-zinc-900/50 bg-gradient-to-r from-red-400 to-red-600 size-6 flex items-center justify-center rounded-lg">
              {violationCount > 99 ? "99+" : violationCount}
            </div>
          </div>

          <div className="flex items-center gap-1 flex-col">
            <ShieldAlert className="size-6 text-red-600" />
            <span className="text-xs font-semibold! text-red-500">
              위반사항
            </span>
          </div>
        </>
      )}
    </button>
  );
}

export default ViolationFloatingButton;
